<template>
  <div class="selector-tree-item">
    <div
      class="selector-node"
      :class="{
        'selector-node--selected': isSelected,
        'selector-node--selectable': canSelectThis,
        'selector-node--parent': !isLeaf,
        'selector-node--locked': isLeaf && !canSelectThis,
      }"
      :style="{ paddingLeft: 12 + depth * 20 + 'px' }"
      @click="handleClick"
    >
      <!-- expand/collapse para pais -->
      <button
        v-if="!isLeaf"
        class="btn-expand"
        @click.stop="expanded = !expanded"
      >
        <i :class="expanded ? 'fas fa-chevron-down' : 'fas fa-chevron-right'" />
      </button>
      <span v-else class="leaf-dot" />

      <!-- ícone do tipo -->
      <div class="si-icon" :style="{ color: categoryColor }">
        <feather-icon :icon="categoryIcon" size="16" />
      </div>

      <!-- nome e tipo -->
      <div class="si-body">
        <span class="si-name">{{ node.name }}</span>
        <span
          class="si-badge"
          v-if="node.type"
          :style="{ color: categoryColor }"
        >
          {{ node.type.name }}
        </span>
      </div>

      <!-- indicadores à direita -->
      <div class="si-right">
        <!-- selecionado (folha ou nó pai em modo supervisão) -->
        <feather-icon
          v-if="isSelected"
          icon="CheckCircleIcon"
          size="16"
          class="text-success"
        />
        <!-- nó pai: mostra contagem de filhos -->
        <span v-else-if="!isLeaf" class="si-children-count text-muted">
          {{ node.children.length }} sub-nó{{
            node.children.length > 1 ? "s" : ""
          }}
        </span>
        <!-- nó folha sem acesso -->
        <feather-icon
          v-else-if="!canSelectThis"
          icon="LockIcon"
          size="13"
          class="text-muted"
        />
      </div>
    </div>

    <!-- filhos recursivos -->
    <template v-if="!isLeaf && expanded">
      <node-selector-item
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :depth="depth + 1"
        :selected-id="selectedId"
        :can-select="canSelect"
        :accessible-ids="accessibleIds"
        @select="$emit('select', $event)"
      />
    </template>
  </div>
</template>

<script>
export default {
  name: "NodeSelectorItem",

  props: {
    node: { type: Object, required: true },
    depth: { type: Number, default: 0 },
    selectedId: { type: [Number, String], default: null },
    // para admin: true (pode selecionar qualquer folha)
    // para não-admin: não usado diretamente, usa accessibleIds
    canSelect: { type: Boolean, default: true },
    // IDs dos nós que o usuário tem acesso (via node_users)
    accessibleIds: { type: Array, default: () => [] },
  },

  data() {
    return { expanded: true };
  },

  computed: {
    isLeaf() {
      return !this.node.children || this.node.children.length === 0;
    },
    isSelected() {
      return this.selectedId === this.node.id;
    },
    // Folha: admin OU está nos IDs acessíveis.
    // Nó pai ("modo supervisão"): só admin — não-admin continua só-folha.
    canSelectThis() {
      if (this.isLeaf) {
        return this.canSelect || this.accessibleIds.includes(this.node.id);
      }
      return this.canSelect;
    },
    categoryIcon() {
      if (!this.node.type) return "CircleIcon";
      const cat = this.node.type.category;
      if (cat === "team") return "UsersIcon";
      if (cat === "professional") return "UserIcon";
      return "GitBranchIcon";
    },
    categoryColor() {
      if (!this.node.type) return "#6c757d";
      const cat = this.node.type.category;
      if (cat === "team") return "#28c76f";
      if (cat === "professional") return "#00cfe8";
      return "#7367f0";
    },
  },

  methods: {
    handleClick() {
      if (!this.isLeaf) {
        if (this.canSelectThis) {
          this.$emit("select", this.node);
          return;
        }
        this.expanded = !this.expanded;
        return;
      }
      if (this.canSelectThis) {
        this.$emit("select", this.node);
      }
    },
  },
};
</script>

<style scoped>
.selector-tree-item {
  user-select: none;
}

.selector-node {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 7px;
  padding-bottom: 7px;
  padding-right: 12px;
  border-radius: 6px;
  transition: background 0.12s;
}

/* PAI: não selecionável, só expande */
.selector-node--parent {
  cursor: pointer;
  color: #555;
}
.selector-node--parent:hover {
  background: #f5f5f5;
}

/* FOLHA COM ACESSO: selecionável */
.selector-node--selectable {
  cursor: pointer;
}
.selector-node--selectable:hover {
  background: #f5f3ff;
}

/* FOLHA SELECIONADA */
.selector-node--selected {
  background: #ede8ff;
}

/* FOLHA SEM ACESSO */
.selector-node--locked {
  cursor: default;
  opacity: 0.45;
}

.btn-expand {
  background: none;
  border: none;
  padding: 0;
  width: 16px;
  font-size: 9px;
  color: #888;
  cursor: pointer;
  flex-shrink: 0;
}

.leaf-dot {
  width: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.leaf-dot::after {
  content: "";
  display: block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #ccc;
}

.selector-node--selectable .leaf-dot::after,
.selector-node--selected .leaf-dot::after {
  background: #7367f0;
}

.si-icon {
  flex-shrink: 0;
}

.si-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.si-name {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.si-badge {
  font-size: 0.7rem;
  font-weight: 600;
}

.si-right {
  flex-shrink: 0;
  font-size: 0.72rem;
}

.si-children-count {
  font-size: 0.7rem;
}
</style>
